export class ProductFilter {
  constructor(options = {}) {
    // Validate required options
    if (!options.container || !options.productListSelector) {
      console.error(
        "ProductFilter requires container and productListSelector options"
      );
      return;
    }

    // Handle container - can be selector string or DOM element
    this.container =
      typeof options.container === "string"
        ? document.querySelector(options.container)
        : options.container;

    // Handle product list selector
    this.productListSelector = options.productListSelector || "#product-list";
    this.ajaxUrl = options.ajaxUrl || "/product/indexajax";
    this.fixedParams = options.fixedParams || {};
    this.loadingClass = options.loadingClass || "loading";
    this.syncUrl = options.syncUrl || false;
    this.syncUrlWithCategoryId = options.syncUrlWithCategoryId || false;
    this.instanceId = options.instanceId || "default";
    this.allowedFilters = options.allowedFilters || [
      "s",
      "b",
      "c",
      "a",
      "po",
      "pr",
      "q",
      "stock",
    ];
    this.filters = new Map();
    this.page = options.page || 1;
    this.total = options.total || 0;
    this.limit = options.limit || 40;

    // Debounce settings - different delays for desktop vs mobile
    this.isMobile = options.isMobile || false;
    this.debounceDelay = options.debounceDelay || (this.isMobile ? 2000 : 500); // 2s mobile, 0.5s desktop
    this.debounceTimer = null;
    this.pendingAjaxCall = false;

    // Special handling for dynamic filters like 'po' (product options)
    this.dynamicFilters = ["po"]; // Filters that use filter-key structure

    // Initialize filters from options if provided
    if (options.filters && typeof options.filters === "object") {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (this.allowedFilters.includes(key) && value) {
          this.filters.set(key, String(value));
        }
      });
    }

    // Initialize category filter if provided
    this.categoryId = options.categoryId || 0;
    if (this.categoryId) {
      this.filters.set("c", this.categoryId);
    }

    // Add instance ID to container and product list
    if (this.container) {
      this.container.dataset.filterInstance = this.instanceId;
    }

    const productList = document.querySelector(this.productListSelector);
    if (productList) {
      productList.dataset.filterInstance = this.instanceId;
    }

    this.init();
  }

  init() {
    if (!this.container) return;

    // Initialize current filters from URL only if syncUrl is enabled
    if (this.syncUrl) {
      const urlParams = new URLSearchParams(window.location.search);
      for (const [key, value] of urlParams.entries()) {
        if (this.allowedFilters.includes(key)) {
          this.filters.set(key, value);
        }
      }
    }

    // Initialize active states based on current filters
    this.initializeActiveStates();

    // Handle desktop clear filters button
    this.desktopClearButton = this.container.querySelector(
      ".desktop-filter-clear"
    );
    if (this.desktopClearButton) {
      this.desktopClearButton.addEventListener("click", () => {
        this.clearFilters();
      });

      // Initialize button visibility
      this.updateClearButtonVisibility();
    }

    // Handle mobile sorter select dropdown
    this.mobileSorterSelect = this.container.querySelector(
      ".product-sorter-mobile"
    );
    if (this.mobileSorterSelect) {
      this.mobileSorterSelect.addEventListener("change", (e) => {
        const filterType = e.target.dataset.filter;
        const filterValue = e.target.value;

        if (this.allowedFilters.includes(filterType) && filterValue) {
          this.handleFilterClick(
            filterType,
            filterValue,
            false,
            null,
            e.target
          );
        }
      });
    }

    // Handle all filter clicks within this instance's container
    this.container.addEventListener("click", (e) => {
      // Handle Apply Filter button click inside ovtwo dropdown
      const applyBtn = e.target.closest(".ovtwo-filter-apply-btn");
      if (applyBtn) {
        e.preventDefault();
        e.stopPropagation();
        const dropdown = applyBtn.closest(".ovtwo-filter-dropdown");
        if (dropdown) {
          dropdown.classList.remove("active");
          const content = dropdown.querySelector(".ovtwo-filter-dropdown-content");
          if (content) content.classList.add("hidden");
        }
        // Trigger API call now
        this.cancelDebouncedCall();
        this.performFilterAjax(false);
        return;
      }

      // Find the exact filter item that was clicked
      let filterItem = null;
      let element = e.target;

      // First try to find the exact element with data-filter
      while (element && element !== document && element !== this.container) {
        if (element.dataset && element.dataset.filter) {
          filterItem = element;
          break;
        }
        element = element.parentElement;
      }

      // Only proceed if we found a valid filter item
      if (!filterItem) return;

      // Prevent duplicate events and bubbling
      if (e.defaultPrevented) return;
      e.preventDefault();
      e.stopPropagation();

      // Ensure the clicked item belongs to this instance
      const instanceContainer = filterItem.closest(
        `[data-filter-instance="${this.instanceId}"]`
      );
      if (!instanceContainer) {
        return;
      }

      const filterType = filterItem.dataset.filter;
      const filterValue = filterItem.dataset.value;
      const filterKey = filterItem.dataset.filterKey; // For dynamic filters
      const isMultiSelect = filterItem.dataset.multiple === "true";

      // Validate if the filter type is allowed
      if (!this.allowedFilters.includes(filterType)) {
        console.warn(`Filter type "${filterType}" is not allowed`);
        return;
      }

      if (!filterValue) {
        console.warn("No filter value found");
        return;
      }

      // If item is inside a dropdown popup, do not trigger API call until Apply button is clicked
      const isInsideDropdown = !!filterItem.closest(".ovtwo-filter-dropdown-content");
      const shouldTriggerApi = !isInsideDropdown;

      this.handleFilterClick(
        filterType,
        filterValue,
        isMultiSelect,
        filterKey,
        filterItem,
        shouldTriggerApi
      );
    });
  }

  // Helper method to parse dynamic filter value (e.g., "1022:1,2|1023:13,15")
  parseDynamicFilter(filterValue) {
    const groups = {};
    if (!filterValue) return groups;

    const groupParts = filterValue.split("|");

    for (const groupPart of groupParts) {
      if (groupPart.includes(":")) {
        const [groupId, values] = groupPart.split(":");
        if (groupId && values) {
          groups[groupId] = values
            .split(",")
            .map((v) => v.trim())
            .filter((v) => v);
        }
      }
    }

    return groups;
  }

  // Helper method to build dynamic filter value (e.g., "1022:1,2|1023:13,15")
  buildDynamicFilter(groups) {
    const parts = [];
    for (const [groupId, values] of Object.entries(groups)) {
      if (values.length > 0) {
        parts.push(`${groupId}:${values.join(",")}`);
      }
    }
    return parts.join("|");
  }

  // Helper method to get values for a specific group in dynamic filter
  getDynamicFilterValues(filterType, groupId) {
    const filterValue = this.filters.get(filterType);
    if (!filterValue) return [];

    const groups = this.parseDynamicFilter(filterValue);
    return groups[groupId] || [];
  }

  // Helper method to set values for a specific group in dynamic filter
  setDynamicFilterValues(filterType, groupId, values) {
    const groups = this.parseDynamicFilter(this.filters.get(filterType));

    if (values.length > 0) {
      groups[groupId] = values;
    } else {
      delete groups[groupId];
    }

    const newFilterValue = this.buildDynamicFilter(groups);
    if (newFilterValue) {
      this.filters.set(filterType, newFilterValue);
    } else {
      this.filters.delete(filterType);
    }
  }

  /** role="checkbox" on the in-stock filter label — keep in sync with .product-filter-option-active */
  syncStockFilterAria() {
    this.container.querySelectorAll('[data-filter="stock"]').forEach((item) => {
      item.setAttribute(
        "aria-checked",
        item.classList.contains("product-filter-option-active")
          ? "true"
          : "false"
      );
    });
  }

  initializeActiveStates() {
    // Update all filter items based on current filters
    this.container.querySelectorAll("[data-filter]").forEach((item) => {
      const filterType = item.dataset.filter;
      const filterValue = item.dataset.value;
      const filterKey = item.dataset.filterKey;
      const isMultiSelect = item.dataset.multiple === "true";

      if (this.dynamicFilters.includes(filterType) && filterKey) {
        // Handle dynamic filters (e.g., po=1:1,2,2:3,4)
        const currentValues = this.getDynamicFilterValues(
          filterType,
          filterKey
        );
        const valueToCheck = String(filterValue).trim();

        if (currentValues.includes(valueToCheck)) {
          item.classList.add("product-filter-option-active");
        }
      } else if (isMultiSelect) {
        // For regular multi-select filters (e.g., attributes, brands)
        const currentValues = (this.filters.get(filterType) || "")
          .split(",")
          .map((v) => String(v).trim());
        const valueToCheck = String(filterValue).trim();

        if (currentValues.includes(valueToCheck)) {
          item.classList.add("product-filter-option-active");
        }
      } else {
        // For single-select filters (e.g., sort, category)
        if (this.filters.get(filterType) === filterValue) {
          item.classList.add("product-filter-option-active");
        }
      }
    });
    this.syncStockFilterAria();
    this.renderActiveFilterTags();
  }

  updateActiveStates() {
    // First remove all active states
    this.container
      .querySelectorAll(".product-filter-option-active")
      .forEach((item) => {
        item.classList.remove("product-filter-option-active");
      });

    // Remove all aria-selected attributes
    this.container.querySelectorAll("[aria-selected]").forEach((item) => {
      item.removeAttribute("aria-selected");
    });

    // Update mobile sorter select if exists
    if (this.mobileSorterSelect) {
      const sorterValue = this.filters.get("s");
      if (sorterValue) {
        this.mobileSorterSelect.value = sorterValue;
      } else {
        this.mobileSorterSelect.selectedIndex = 0; // Reset to first option
      }
    }

    // Then reapply active states based on current filters
    this.container.querySelectorAll("[data-filter]").forEach((item) => {
      const filterType = item.dataset.filter;
      const filterValue = item.dataset.value;
      const filterKey = item.dataset.filterKey;
      const isMultiSelect = item.dataset.multiple === "true";

      if (this.dynamicFilters.includes(filterType) && filterKey) {
        // Handle dynamic filters (e.g., po=1:1,2,2:3,4)
        const currentValues = this.getDynamicFilterValues(
          filterType,
          filterKey
        );
        const valueToCheck = String(filterValue).trim();

        if (currentValues.includes(valueToCheck)) {
          item.classList.add("product-filter-option-active");
        }
      } else if (isMultiSelect) {
        // For regular multi-select filters (e.g., attributes, brands)
        const currentValues = (this.filters.get(filterType) || "")
          .split(",")
          .map((v) => String(v).trim());
        const valueToCheck = String(filterValue).trim();

        if (currentValues.includes(valueToCheck)) {
          item.classList.add("product-filter-option-active");
        }
      } else {
        // For single-select filters (e.g., sort, category)
        if (this.filters.get(filterType) === filterValue) {
          item.classList.add("product-filter-option-active");
          // Also set aria-selected for sorter options
          if (filterType === "s") {
            item.setAttribute("aria-selected", "true");
          }
        }
      }
    });
    this.syncStockFilterAria();
  }

  showLoading() {
    if (!this.productListSelector) return;

    const listElement = document.querySelector(this.productListSelector);
    if (!listElement) {
      // If there is no product list container (e.g., first load with no results),
      // skip showing loading overlay but still allow AJAX to run.
      return;
    }

    // Create loading overlay if not exists
    if (!this.loadingOverlay) {
      this.loadingOverlay = document.createElement("div");
      this.loadingOverlay.className = "absolute inset-0 bg-white/50 z-50";

      // Create spinner container
      const spinnerContainer = document.createElement("div");
      spinnerContainer.className = "flex items-center justify-center";

      // Create spinner
      const spinner = document.createElement("div");
      spinner.className =
        "animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent";

      spinnerContainer.appendChild(spinner);
      this.loadingOverlay.appendChild(spinnerContainer);
    }

    // Position spinner based on product list height
    const listHeight = listElement.offsetHeight;
    const spinnerContainer = this.loadingOverlay.querySelector("div");

    if (listHeight < 400) {
      // Center vertically if list is short
      spinnerContainer.className = "flex items-center justify-center h-full";
    } else {
      // Position 200px from top if list is tall
      spinnerContainer.className =
        "flex items-center justify-center mt-[200px]";
    }

    // Add loading overlay to product list
    listElement.style.position = "relative";
    listElement.appendChild(this.loadingOverlay);
  }

  hideLoading() {
    if (this.loadingOverlay && this.loadingOverlay.parentNode) {
      this.loadingOverlay.parentNode.removeChild(this.loadingOverlay);
    }
  }

  getFilterParams() {
    const params = {};
    for (const [key, value] of this.filters.entries()) {
      params[key] = value;
    }
    return params;
  }

  // Debounced AJAX call method
  debouncedAjaxCall() {
    // Clear existing timer if any
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Set pending state
    this.pendingAjaxCall = true;

    // Show loading indicator immediately for better UX
    this.showLoading();

    // Dispatch event to notify other components about pending state
    window.dispatchEvent(
      new CustomEvent("productFilterPending", {
        detail: {
          instanceId: this.instanceId,
          pending: true,
          delay: this.debounceDelay,
        },
      })
    );

    // Set new timer
    this.debounceTimer = setTimeout(async () => {
      try {
        await this.performFilterAjax(true); // Skip loading since we already showed it
        this.pendingAjaxCall = false;

        // Dispatch completion event
        window.dispatchEvent(
          new CustomEvent("productFilterPending", {
            detail: {
              instanceId: this.instanceId,
              pending: false,
            },
          })
        );
      } catch (error) {
        console.error("Debounced AJAX call failed:", error);
        this.pendingAjaxCall = false;
        this.hideLoading();

        // Dispatch error event
        window.dispatchEvent(
          new CustomEvent("productFilterPending", {
            detail: {
              instanceId: this.instanceId,
              pending: false,
              error: true,
            },
          })
        );
      }
    }, this.debounceDelay);
  }

  // Cancel debounced call
  cancelDebouncedCall() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    if (this.pendingAjaxCall) {
      this.pendingAjaxCall = false;
      this.hideLoading();
    }
  }

  // Update clear button visibility based on active filters
  updateClearButtonVisibility() {
    if (!this.desktopClearButton) return;

    // Count active filters
    let activeFilterCount = 0;
    for (const [key, value] of this.filters.entries()) {
      // Skip these filters from count:
      // 1. Category filter if on category page or syncUrlWithCategoryId is false
      // 2. Sorter filter - it's UI control, not data filter
      if (key === "c" && (this.categoryId > 0 || !this.syncUrlWithCategoryId)) {
        continue;
      }
      if (key === "s") {
        continue; // Skip sorter
      }

      if (value && value.toString().trim() !== "") {
        activeFilterCount++;
      }
    }

    // Show/hide button based on active filters
    const buttonContainer = this.desktopClearButton.closest(
      ".desktop-filter-actions"
    );
    if (buttonContainer) {
      if (activeFilterCount > 0) {
        buttonContainer.style.display = "block";
      } else {
        buttonContainer.style.display = "none";
      }
    }
  }

  // New method to handle filter click with immediate UI feedback
  async handleFilterClick(
    filterType,
    filterValue,
    isMultiSelect = false,
    filterKey = null,
    clickedElement = null,
    shouldTriggerApi = true
  ) {
    // 1. IMMEDIATE UI UPDATE - Toggle visual state right away
    if (clickedElement) {
      const isCurrentlyActive = clickedElement.classList.contains(
        "product-filter-option-active"
      );

      if (
        isMultiSelect ||
        (this.dynamicFilters.includes(filterType) && filterKey)
      ) {
        // For multi-select and dynamic filters, just toggle the clicked item
        if (isCurrentlyActive) {
          clickedElement.classList.remove("product-filter-option-active");
        } else {
          clickedElement.classList.add("product-filter-option-active");
        }
      } else {
        // For single-select, remove active from all similar items first
        this.container
          .querySelectorAll(`[data-filter="${filterType}"]`)
          .forEach((item) => {
            item.classList.remove("product-filter-option-active");
            // Also remove aria-selected for sorter options
            if (filterType === "s") {
              item.removeAttribute("aria-selected");
            }
          });

        // Then add active to clicked item (unless it was already active)
        if (!isCurrentlyActive) {
          clickedElement.classList.add("product-filter-option-active");
          // Also set aria-selected for sorter options
          if (filterType === "s") {
            clickedElement.setAttribute("aria-selected", "true");
          }
        }
      }
    }

    this.syncStockFilterAria();

    // 2. UPDATE FILTERS IN MEMORY
    this.updateFiltersInMemory(
      filterType,
      filterValue,
      isMultiSelect,
      filterKey
    );

    // 3. RENDER ACTIVE FILTER TAGS & UPDATE CLEAR BUTTON
    this.renderActiveFilterTags();
    this.updateClearButtonVisibility();

    // 4. AJAX CALL - Only if shouldTriggerApi is true or sorter
    if (shouldTriggerApi || filterType === "s") {
      if (filterType === "s") {
        this.cancelDebouncedCall();
        this.performFilterAjax(false);
      } else {
        this.debouncedAjaxCall();
      }
    }
  }

  // Render selected filter tags (pills below filter buttons) and update dropdown counts
  renderActiveFilterTags() {
    const tagsContainer = this.container.querySelector(".ovtwo-active-filter-tags");
    const dropdownCounts = new Map();

    if (tagsContainer) {
      tagsContainer.innerHTML = "";
    }

    let activeTagCount = 0;
    const processedKeys = new Set();

    this.container.querySelectorAll(".product-filter-option-active").forEach((item) => {
      const filterType = item.dataset.filter;
      const filterValue = item.dataset.value;
      const filterKey = item.dataset.filterKey;
      const isMultiSelect = item.dataset.multiple === "true";

      const dropdown = item.closest(".ovtwo-filter-dropdown");
      if (dropdown) {
        dropdownCounts.set(dropdown, (dropdownCounts.get(dropdown) || 0) + 1);
      }

      if (!tagsContainer) return;

      const uniqueKey = `${filterType}-${filterKey || ""}-${filterValue}`;
      if (processedKeys.has(uniqueKey)) return;
      processedKeys.add(uniqueKey);

      activeTagCount++;

      const labelSpan = item.querySelector(".product-filter-option-product-option-label");
      const labelText = labelSpan ? labelSpan.textContent.trim() : (item.getAttribute("title") || filterValue).trim();

      const tagEl = document.createElement("div");
      tagEl.className = "ovtwo-active-tag bg-[#7564A7] hover:bg-[#5f4e8c] text-white text-[11px] font-bold font-mono px-3 py-1.5 inline-flex items-center gap-2 uppercase tracking-wider cursor-pointer transition-colors select-none";
      tagEl.innerHTML = `
        <span>${labelText}</span>
        <span class="ovtwo-active-tag-remove font-bold ml-1 hover:text-gray-200">✕</span>
      `;

      tagEl.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleFilterClick(filterType, filterValue, isMultiSelect, filterKey, item, true);
      });

      tagsContainer.appendChild(tagEl);
    });

    if (tagsContainer) {
      if (activeTagCount > 0) {
        tagsContainer.classList.remove("hidden");
      } else {
        tagsContainer.classList.add("hidden");
      }
    }

    // Update header labels on dropdown triggers (e.g. "ORIGIN (1)")
    this.container.querySelectorAll(".ovtwo-filter-dropdown").forEach((dropdown) => {
      const triggerLabel = dropdown.querySelector(".ovtwo-filter-dropdown-label");
      if (!triggerLabel) return;

      if (!dropdown.dataset.originalLabel) {
        dropdown.dataset.originalLabel = triggerLabel.textContent.replace(/\s*\(\d+\)$/, "").trim();
      }
      const baseLabel = dropdown.dataset.originalLabel;
      const count = dropdownCounts.get(dropdown) || 0;

      if (count > 0) {
        triggerLabel.textContent = `${baseLabel} (${count})`;
      } else {
        triggerLabel.textContent = baseLabel;
      }
    });
  }

  // Extract filter update logic to separate method
  updateFiltersInMemory(
    filterType,
    filterValue,
    isMultiSelect = false,
    filterKey = null
  ) {
    if (this.dynamicFilters.includes(filterType) && filterKey) {
      // Handle dynamic filters (e.g., po=1:1,2,2:3,4)
      let currentValues = this.getDynamicFilterValues(filterType, filterKey);
      const valueToAdd = String(filterValue).trim();
      const valueIndex = currentValues.findIndex(
        (v) => String(v).trim() === valueToAdd
      );

      if (valueIndex === -1) {
        // Add new value
        currentValues.push(valueToAdd);
      } else {
        // Remove existing value (toggle behavior)
        currentValues.splice(valueIndex, 1);
      }

      // Update the dynamic filter
      this.setDynamicFilterValues(filterType, filterKey, currentValues);
    } else if (isMultiSelect) {
      // Handle regular multiple selection (e.g., attributes, brands)
      let currentValues = [];
      const existingFilter = this.filters.get(filterType);

      if (existingFilter) {
        currentValues = existingFilter.split(",").map((v) => v.trim());
      }

      // Convert filterValue to string for consistent comparison
      const valueToAdd = String(filterValue).trim();
      const valueIndex = currentValues.findIndex(
        (v) => String(v).trim() === valueToAdd
      );

      if (valueIndex === -1) {
        // Add new value
        currentValues.push(valueToAdd);
      } else {
        // Remove existing value
        currentValues.splice(valueIndex, 1);
      }

      // Update or remove the filter
      if (currentValues.length > 0) {
        const newFilterValue = currentValues.join(",");
        this.filters.set(filterType, newFilterValue);
      } else {
        this.filters.delete(filterType);
      }
    } else {
      // Handle single selection (e.g., sort, category)
      const currentValue = this.filters.get(filterType);

      if (currentValue === filterValue) {
        // If clicking the same value, remove it
        this.filters.delete(filterType);
      } else {
        // Set new value
        this.filters.set(filterType, filterValue);
      }
    }

    // Reset page to 1 for filter changes
    this.page = 1;
  }

  // Separate AJAX method for better performance
  async performFilterAjax(skipLoading = false) {
    try {
      // Only show loading if not already shown by debounce
      if (!skipLoading && !this.pendingAjaxCall) {
        this.showLoading();
      }

      // Build URL params manually to avoid encoding commas
      const params = [];

      // Add fixed params that should be sent to AJAX but not synced to URL.
      Object.entries(this.fixedParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.push(`${encodeURIComponent(key)}=${value}`);
        }
      });

      // Add all current filters to params
      for (const [key, value] of this.filters.entries()) {
        // Only encode the key, not the value to preserve commas
        const encodedKey = encodeURIComponent(key);
        params.push(`${encodedKey}=${value}`);
      }

      // Add pagination params if page > 1
      if (this.page > 1) {
        params.push(`page=${this.page}`);
      }

      const urlParams = params.join("&");

      // Scroll to top if syncUrl is enabled
      if (this.syncUrl) {
        window.scrollTo(0, 0);
      }

      const response = await fetch(
        `${this.ajaxUrl}${urlParams ? "?" + urlParams : ""}`
      );
      const data = await response.json();
      const payload = data?.data || {};

      // Initialize hasNewContent outside the if block
      let hasNewContent = false;

      if (payload.total !== undefined) {
        // Update total in memory
        this.total = payload.total;

        // Update total display in DOM
        const totalElements = this.container.querySelectorAll(
          ".product-list-total"
        );
        totalElements.forEach((element) => {
          element.textContent = payload.total;
          element.dataset.total = payload.total;
        });

        // Update product list if there's HTML content
        const productList = document.querySelector(this.productListSelector);

        if (productList && payload.html) {
          productList.innerHTML = payload.html;
          hasNewContent = true;
          // Update pagination state if provided
          if (payload.page) this.page = payload.page;
        }
      }

      // Update URL only if syncUrl is enabled
      if (this.syncUrl) {
        const newUrl = new URL(window.location.href);

        // Build URL params for sync, excluding category if syncUrlWithCategoryId is false
        const syncParams = [];

        // Add all current filters to params
        for (const [key, value] of this.filters.entries()) {
          // Skip category filter in these cases:
          // 1. If we're on a category page (categoryId > 0) - URL already has category slug
          // 2. If syncUrlWithCategoryId is false - category shouldn't be in URL
          if (
            key === "c" &&
            (this.categoryId > 0 || !this.syncUrlWithCategoryId)
          ) {
            continue;
          }

          // Only encode the key, not the value to preserve commas
          const encodedKey = encodeURIComponent(key);
          syncParams.push(`${encodedKey}=${value}`);
        }

        // Add pagination params if page > 1
        if (this.page > 1) {
          syncParams.push(`page=${this.page}`);
        }

        newUrl.search = syncParams.join("&");
        window.history.pushState({}, "", newUrl);
      }

      // Dispatch custom event for other components
      window.dispatchEvent(
        new CustomEvent("productFilterChanged", {
          detail: {
            instanceId: this.instanceId,
            filters: Object.fromEntries(this.filters),
            total: this.total,
            page: this.page,
          },
        })
      );

      // Also dispatch a specific event for wishlist reinitialization
      window.dispatchEvent(
        new CustomEvent("productListUpdated", {
          detail: {
            instanceId: this.instanceId,
            hasNewContent: hasNewContent,
          },
        })
      );
    } catch (error) {
      console.error("Error applying filters:", error);
      // If AJAX fails, revert UI state by calling updateActiveStates
      this.updateActiveStates();
    } finally {
      this.hideLoading();
    }
  }

  // Keep original handleFilter for backward compatibility (pagination, etc.)
  async handleFilter(
    filterType,
    filterValue,
    isMultiSelect = false,
    filterKey = null
  ) {
    if (typeof filterType === "undefined") {
      // This is likely a pagination change, just call AJAX
      await this.performFilterAjax();
    } else {
      // This is a filter change, update memory and call AJAX
      this.updateFiltersInMemory(
        filterType,
        filterValue,
        isMultiSelect,
        filterKey
      );
      await this.performFilterAjax();
      // Don't call updateActiveStates here - UI state is already correct from handleFilterClick
    }
  }

  // Helper method to get current filters
  getFilters() {
    return Object.fromEntries(this.filters);
  }

  // Helper method to clear all filters (except category in certain cases)
  clearFilters() {
    // Cancel any pending debounced call
    this.cancelDebouncedCall();

    // Preserve filters that shouldn't be cleared:
    // 1. Category filter if on category page or syncUrlWithCategoryId is false
    // 2. Sorter filter - it's UI control, not data filter
    const categoryFilter = this.filters.get("c");
    const sorterFilter = this.filters.get("s");
    const shouldPreserveCategory =
      (this.categoryId > 0 || !this.syncUrlWithCategoryId) && categoryFilter;

    this.filters.clear();

    // Restore preserved filters
    if (shouldPreserveCategory) {
      this.filters.set("c", categoryFilter);
    }
    if (sorterFilter) {
      this.filters.set("s", sorterFilter);
    }

    this.page = 1;

    // Update UI to reflect cleared filters
    this.updateActiveStates();
    this.updateClearButtonVisibility();

    // Call AJAX to update results
    this.handleFilter();
  }

  // Helper method to clear specific filter type
  clearFilterType(filterType) {
    this.filters.delete(filterType);

    // Update UI to reflect cleared filter
    this.updateActiveStates();
    this.updateClearButtonVisibility();

    // Call AJAX to update results
    this.handleFilter();
  }

  // Public method to apply filter programmatically
  applyFilter(filterType, filterValue, isMultiSelect = false) {
    // Validate if the filter type is allowed
    if (!this.allowedFilters.includes(filterType)) {
      console.warn(`Filter type "${filterType}" is not allowed`);
      return;
    }

    return this.handleFilter(filterType, filterValue, isMultiSelect);
  }

  // Public method to set search keyword
  setSearchKeyword(keyword) {
    if (keyword && keyword.trim()) {
      this.filters.set("q", keyword.trim());
    } else {
      this.filters.delete("q");
    }
    this.page = 1; // Reset to first page when searching
    return this.handleFilter();
  }

  // Public method to get current search keyword
  getSearchKeyword() {
    return this.filters.get("q") || "";
  }

  // Add new methods for pagination
  setPage(page) {
    this.page = page;
    this.handleFilter();
  }

  getTotalPages() {
    return Math.ceil(this.total / this.limit);
  }

  hasNextPage() {
    return this.page < this.getTotalPages();
  }

  hasPreviousPage() {
    return this.page > 1;
  }

  // Update getState to include pagination info
  getState() {
    return {
      filters: Object.fromEntries(this.filters),
      page: this.page,
      total: this.total,
      totalPages: this.getTotalPages(),
    };
  }
}
